import { Component, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { finalize, Subscription } from 'rxjs';
import { DynamicFormComponent } from 'src/app/components/dynamic-form/dynamic-form.component';
import { LoginOptionComponent } from 'src/app/components/forgot-password/login-option.component';
import { AuthService } from 'src/app/services/auth.service';
import { ToastService } from 'src/app/services/toast.service';
import { PATTERN, ROUTES } from 'src/app/shared/constants';
import { DIRECTION, FormControlType } from 'src/app/shared/enums';
import { IFormConfig } from 'src/app/shared/interfaces/IForm';

@Component({
  selector: 'app-reset-password',
  imports: [RouterModule, DynamicFormComponent],
  templateUrl: './reset-password.component.html'
})
export class ResetPasswordComponent implements OnDestroy {
  @ViewChild(DynamicFormComponent) dynamicFormComponent: DynamicFormComponent;
  passwordSubscription = new Subscription();
  resetPasswordForm: FormGroup;
  formConfig: IFormConfig;
  spinLoader = false;
  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private toastService: ToastService) {
      this.initForm();
  }

  get token() {
    return this.route.snapshot.queryParamMap.get('params');
  }

  initForm() {
    this.resetPasswordForm = this.formBuilder.group(
      {
        newPassword: [null, [Validators.required, Validators.pattern(PATTERN.PASSWORD)]],
        confirmPassword: [null, [Validators.required, this.passwordMatchValidator]]
      }
    );
    this.passwordSubscription.add(
      this.resetPasswordForm.get('newPassword')!.valueChanges.subscribe(() => {
        this.resetPasswordForm.get('confirmPassword')!.updateValueAndValidity();
      })
    );

    this.formConfig = {
      id: 'resetPasswordForm',
      actions: [
        {
          text: 'save',
          type: 'submit',
          class: 'w-full mt-4!'
        }
      ],
      items: [
        {
          key: 'newPassword',
          label: 'new password',
          layoutConfig: [{ column: 12 }],
          labelDirection: DIRECTION.INSIDE,
          inputConfig: { isPassword: true },
          controlType: FormControlType.INPUT,
          validator: [
            { errorType: 'required', errorMessage: 'new password required' },
            { errorType: 'pattern', errorMessage: 'must include uppercase number and special' }
          ]
        },
        {
          key: 'confirmPassword',
          label: 'confirm password',
          layoutConfig: [{ column: 12 }],
          labelDirection: DIRECTION.INSIDE,
          inputConfig: { isPassword: true },
          controlType: FormControlType.INPUT,
          itemClass: 'mt-1!',
          validator: [
            { errorType: 'required', errorMessage: 'confirm password required' },
            { errorType: 'passwordMismatch', errorMessage: 'passwords do not match' }
          ]
        },
        {
          key: 'login-option',
          layoutConfig: [{ column: 12 }],
          controlType: FormControlType.DYNAMIC_COMPONENT,
          dynamicComponentConfig: {
            component: LoginOptionComponent,
            inputs: {
              text: 'back to login',
              action: () => {
                this.router.navigate([ROUTES.USER.LOGIN]);
              }
            }
          }
        }
      ]
    };
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.parent?.get('newPassword')?.value;
    if (control.value && newPassword !== control.value) {
      return { passwordMismatch: true };
    }
    return null;
  }

  ngOnDestroy() {
    this.passwordSubscription.unsubscribe();
  }

  onSubmit = () => {
    if (this.resetPasswordForm.invalid) return this.resetPasswordForm.markAllAsTouched();
    this.spinLoader = true;
    this.authService
      .resetPassword({ ...this.resetPasswordForm.value, token: this.token })
      .pipe(finalize(() => (this.spinLoader = false)))
      .subscribe({
        next: () => {
          this.toastService.success(this.translateService.instant('update successfully'));
          this.router.navigate([ROUTES.USER.LOGIN]);
        },
        error: ({ error }) => {
          this.dynamicFormComponent.ValidatorError = error;
        }
      });
  };
}
